/* ──────────────────────────────────────────────────────────────────────────
   Shoppable Video Section
   ──────────────────────────────────────────────────────────────────────────

   Custom element rendering a coverflow carousel of product videos. Built as
   an infinite loop using clone-end buffering — same pattern Swiper uses in
   loop mode, implemented in plain JS so we can keep the bespoke shoppable
   behaviour (full-video overlay, variant picker, ATC, mute toggle).

   DOM layout (for N real blocks rendered by Liquid):

       [clone-0 .. clone-N-1]  [real-0 .. real-N-1]  [clone-0 .. clone-N-1]
       └── prefix (N slides) ─┘└── real (N slides) ──┘└── suffix (N slides) ─┘

   Total slides in DOM = 3N. The active slide always lives in the real
   region after settle. Navigation that crosses into a clone region (e.g.
   prev on real-0 lands on prefix-clone-N-1) is silently teleported to the
   matching real slide by _teleportToReal in the settle handler — invisible
   because every visible neighbour around the clone has an identical-looking
   twin around the real, and all paused real videos are reset to currentTime=0
   in _pauseAll so they display the same first frame as the cloned posters.

   Visibility rule: any slide at data-dist >= 3 is hidden via CSS opacity:0.
   The visible window is therefore exactly 5 cards (active + 2 each side =
   distances 0,1,2), matching the original carousel even though the DOM now
   contains 3N total slides.

   Concurrent decoders: capped at 5 (active full + 4 visible inactive thumbs)
   by _playActive's `nDist > 2` skip. The buffered clones at dist 3 sit
   pre-loaded but paused, so they can start playing instantly the moment they
   cross into the visible window during a wraparound transition.
   ────────────────────────────────────────────────────────────────────────── */

const SV_MAX_DIST = 3
const SV_SETTLE_FALLBACK_MS = 450

class ShoppableVideoSection extends HTMLElement {
  connectedCallback() {
    // Hide the section briefly while we inject clones so the user never sees
    // the un-cloned 5-slide layout flash before JS runs. The class is removed
    // on the next frame, so if JS fails to run the section just stays visible.
    this.classList.add('sv-initializing')

    this._muted = true
    this._loaded = false
    this._hintShown = false
    this._sectionVisible = false
    this._settleHandler = null
    this._settleTimer = 0
    this._staggerTimer = 0
    this._isAnimating = false
    this._currentTranslateX = 0
    this._cachedGap = null
    this._track = this.querySelector('.sv-track')
    this._viewport = this.querySelector('.sv-viewport')

    // Capture the original real slides BEFORE cloning. _realSlides stays as a
    // reference to just the real slide elements (sorted by logical index) for
    // the lifetime of the section. _count comes from the schema-set attribute.
    this._realSlides = [...this._track.querySelectorAll('.sv-slide')]
      .sort((a, b) => this._getLogicalIndex(a) - this._getLogicalIndex(b))
    this._realCount = this._realSlides.length
    this._count = parseInt(this.dataset.count, 10) || this._realCount
    if (!this._track || !this._viewport || !this._realCount) return

    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

    // Bind interactive handlers to REAL slides BEFORE cloning. cloneNode does
    // not copy event listeners, so the clones we create next stay non-
    // interactive (which is what we want — they're decorative duplicates that
    // exist purely to make the wraparound teleport invisible).
    this._setupSlideClicks()
    this._setupAtcButtons()
    this._setupMuteBtn()
    this._setupVariantPickers()

    // Inject prefix + suffix clones so the carousel has buffer slides on both
    // sides of the real slides. After this _slides has 3 × _realCount entries:
    //   [prefix clones] [real slides] [suffix clones]
    this._setupClones()
    this._slides = [...this._track.querySelectorAll('.sv-slide')]

    // Initial active is the middle real slide. When there's only 1 real slide
    // we skip cloning entirely (see _setupClones), so the real region starts
    // at DOM index 0; otherwise it starts at _realCount (after the prefix
    // clones). Anchor index = active index initially.
    const cloneOffset = this._realCount >= 2 ? this._realCount : 0
    this._activeIndex = cloneOffset + Math.floor((this._realCount - 1) / 2)
    this._anchorIndex = this._activeIndex

    this._setActive(this._activeIndex, false)
    this._updateArrows()
    this._setupSectionIO()
    this._setupArrows()
    this._setupTouchSwipe()
    this._setupResize()
    this._setupThemeEditor()

    // Reveal once the cloned layout is committed so the user only ever sees
    // the final 15-slide track, not the original 5-slide flash.
    requestAnimationFrame(() => this.classList.remove('sv-initializing'))
  }

  disconnectedCallback() {
    this._clearPendingSettle()
    if (this._staggerTimer) clearTimeout(this._staggerTimer)
    this._sectionObserver?.disconnect()
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler)
    if (this._visibilityHandler) document.removeEventListener('visibilitychange', this._visibilityHandler)
    if (this._themeEditorHandler) document.removeEventListener('shopify:block:select', this._themeEditorHandler)
  }

  _getLogicalIndex(slide) {
    return parseInt(slide?.dataset.index, 10) || 0
  }

  _getTrackGap() {
    if (this._cachedGap !== null) return this._cachedGap
    const styles = window.getComputedStyle(this._track)
    const gap = parseFloat(styles.columnGap || styles.gap || '0')
    this._cachedGap = Number.isFinite(gap) ? gap : 0
    return this._cachedGap
  }

  _applyCircularOrder(activeLogicalIndex) {
    const start = this._mod(activeLogicalIndex - this._anchorIndex, this._count)
    const ordered = []

    for (let offset = 0; offset < this._count; offset++) {
      ordered.push(this._realSlides[this._mod(start + offset, this._count)])
    }

    this._track.append(...ordered)
    this._slides = ordered
    this._activeIndex = this._slides.findIndex(slide => this._getLogicalIndex(slide) === activeLogicalIndex)
    this._activeLogicalIndex = activeLogicalIndex
  }

  /* ── Infinite-loop clones ──
     We render N "real" slides via Liquid, then JS prepends a copy of all N
     and appends another copy of all N, producing a 3N strip:
       [clone-0 .. clone-N-1] [real-0 .. real-N-1] [clone-0 .. clone-N-1]
     The active slide always lives in the real region. When navigation lands
     on a clone (one slide past the real region), the settle handler silently
     teleports to the matching real slide via _teleportToReal — invisible to
     the user because every visible neighbour around the clone has an
     identical-looking twin at the same offset around the real. */
  _setupClones() {
    if (this._realCount < 2) return

    const prefix = this._realSlides.map(slide => this._cloneSlide(slide, 'prev'))
    const suffix = this._realSlides.map(slide => this._cloneSlide(slide, 'next'))

    // Prepend in reverse so the order in the DOM matches realSlides order.
    this._track.prepend(...prefix)
    this._track.append(...suffix)
  }

  _cloneSlide(slide, position) {
    const clone = slide.cloneNode(true)
    clone.dataset.clone = position

    // Strip the actual src attribute so the clone doesn't auto-decode on
    // creation. KEEP data-src so _injectSrc can lazy-load the source the
    // moment the clone enters the visible window during a wraparound — that
    // way the briefly-visible cloned neighbours are playing the same short
    // muted loop as their real twins, and the teleport is invisible.
    // Concurrent decoder count stays bounded at ~5 (active full + 4 visible
    // inactive thumbs) because _playActive only plays slides at dist ≤ 2.
    clone.querySelectorAll('video').forEach(v => {
      v.removeAttribute('src')
    })

    return clone
  }

  /* Find the real-region DOM index that matches a clone's logical index. */
  _findRealDomIndex(logicalIndex) {
    const start = this._realCount
    const end = this._realCount * 2
    for (let i = start; i < end; i++) {
      if (this._getLogicalIndex(this._slides[i]) === logicalIndex) return i
    }
    return -1
  }

  /* Silently jump from a clone to its real twin. Both slides occupy the same
     visual position around the active centre (because all the visible
     neighbours are also clone/real twins of each other), so this teleport is
     invisible — the user never sees it. */
  _teleportToReal(cloneSlide) {
    const logicalIdx = this._getLogicalIndex(cloneSlide)
    const realDomIdx = this._findRealDomIndex(logicalIdx)
    if (realDomIdx < 0) return

    // Disable BOTH the track transform transition AND each slide's opacity
    // transition. The teleport flips data-dist values for many slides at once
    // (the "old visible" 5 cards each shift to dist≥3 and the "new visible" 5
    // cards each shift down to dist≤2), and with opacity transitions enabled
    // those changes would cross-fade for 0.3s, briefly showing two
    // overlapping semi-transparent layers. We want the swap to commit in a
    // single frame so the user sees nothing change.
    this._track.style.transition = 'none'
    this._slides.forEach(s => { s.style.transition = 'none' })

    this._setActive(realDomIdx, false, { resetPlaying: false })

    // Force a synchronous layout flush so the no-transition transform and
    // opacity changes commit before we re-enable transitions on the next frame.
    void this._track.offsetWidth
    requestAnimationFrame(() => {
      this._track.style.transition = ''
      this._slides.forEach(s => { s.style.transition = '' })
    })
  }

  _injectSrc(slide) {
    const video = slide.querySelector('video')
    if (!video || !video.dataset.src || video.src) return
    video.preload = 'metadata'
    video.loop = true
    video.src = video.dataset.src
    video.load()

    if (!slide._svEndedBound) {
      slide._svEndedBound = true
      video.addEventListener('ended', () => {
        const dist = parseInt(slide.dataset.dist, 10)
        if (dist > 1) return
        video.currentTime = 0
        video.play().catch(() => { })
        slide.dataset.playing = 'true'
      })
    }
  }

  _injectSrcForRange(centerDom, radius) {
    for (let i = centerDom - radius; i <= centerDom + radius; i++) {
      if (i >= 0 && i < this._slides.length) this._injectSrc(this._slides[i])
    }
  }

  _setupSectionIO() {
    this._sectionObserver = new IntersectionObserver(([e]) => {
      this._sectionVisible = e.isIntersecting
      if (e.isIntersecting && !document.hidden) {
        if (!this._loaded) {
          this._loaded = true

          // On metered / data-saver connections, skip video autoplay entirely — show posters only
          const conn = navigator.connection
          if (conn && (conn.saveData || conn.effectiveType === '2g')) return

          // Load active + neighbors first (immediate visual), then stagger
          // ALL slides (including clones) so they're ready to play the moment
          // they cross into the visible window during a wraparound. The total
          // decoder count is still bounded at ~5 by _playActive (which only
          // starts playback on slides at dist ≤ 2) — the rest just sit
          // pre-buffered.
          this._injectSrcForRange(this._activeIndex, 1)
          this._staggerTimer = setTimeout(() => this._injectSrcForRange(this._activeIndex, this._slides.length), 1500)
        }
        this._playActive()
      } else {
        this._pauseAll()
      }
    }, { threshold: 0.15 })
    this._sectionObserver.observe(this)

    this._visibilityHandler = () => {
      if (document.hidden) {
        this._pauseAll()
      } else if (this._sectionVisible) {
        this._playActive()
      }
    }
    document.addEventListener('visibilitychange', this._visibilityHandler)
  }

  _setActive(domIndex, animate = true, { resetPlaying = true } = {}) {
    const activeSlide = this._slides[domIndex]
    if (!activeSlide) return
    this._activeIndex = domIndex
    this._activeLogicalIndex = this._getLogicalIndex(activeSlide)
    this._slides.forEach((slide, i) => {
      slide.dataset.dist = Math.min(Math.abs(i - domIndex), SV_MAX_DIST)
      if (resetPlaying) slide.dataset.playing = 'false'
    })
    if (!resetPlaying) {
      const activeVideo = activeSlide.querySelector('video')
      activeSlide.dataset.playing = activeVideo && !activeVideo.paused ? 'true' : activeSlide.dataset.playing || 'false'
    }
    this._repositionTrack(animate)
  }

  _repositionTrack(animate = true) {
    if (!animate) this._track.style.transition = 'none'

    const vpW = this._viewport.offsetWidth
    const gap = this._getTrackGap()
    let offset = 0
    this._slides.forEach((slide, i) => {
      if (i < this._activeIndex) offset += slide.offsetWidth + gap
    })

    const activeW = this._slides[this._activeIndex]?.offsetWidth || 0
    const tx = -(offset - (vpW - activeW) / 2)

    // CWV: do all layout READS (arrow positions read slide offsetWidths) BEFORE the
    // track transform WRITE, to avoid read-after-write layout thrashing on every slide.
    this._updateArrowPositions(vpW, gap)
    this._setTrackTransform(tx)
    if (!animate) requestAnimationFrame(() => { this._track.style.transition = '' })
  }

  _updateArrowPositions(vpW, gap) {
    if (!this._prevArrow || !this._nextArrow) return

    const halfVp = vpW / 2
    const halfActive = (this._slides[this._activeIndex]?.offsetWidth || 0) / 2
    const arrowW = this._prevArrow.offsetWidth || 40
    const pad = 12

    // How far content extends from the viewport centre toward each edge.
    // Left = slides before the active, right = slides after.
    let leftExtent = halfActive
    for (let i = this._activeIndex - 1; i >= 0; i--)
      leftExtent += gap + this._slides[i].offsetWidth

    let rightExtent = halfActive
    for (let i = this._activeIndex + 1; i < this._slides.length; i++)
      rightExtent += gap + this._slides[i].offsetWidth

    // Empty space between the outermost slide and the viewport edge, per side
    const leftGap = Math.max(0, halfVp - leftExtent)
    const rightGap = Math.max(0, halfVp - rightExtent)

    // If there's enough room outside the content for the arrow + padding,
    // sit the arrow in that gap (no overlap). Otherwise fall back to the
    // available gap or a minimum of 8px — only dimmed edge slides overlap.
    this._prevArrow.style.left = (leftGap > arrowW + pad
      ? leftGap - arrowW - pad
      : Math.max(8, leftGap)) + 'px'

    this._nextArrow.style.right = (rightGap > arrowW + pad
      ? rightGap - arrowW - pad
      : Math.max(8, rightGap)) + 'px'
  }

  _setTrackTransform(tx) {
    this._currentTranslateX = tx
    this._track.style.transform = `translateX(${tx}px)`
  }

  _pauseAll() {
    this._slides.forEach(s => {
      const thumb = s.querySelector('video')
      if (thumb) {
        thumb.pause()
        // Reset to the first frame so the paused real video displays the
        // same image as its cloned twin (which only ever shows the poster).
        // This is what makes the wraparound teleport visually invisible —
        // every real/clone pair looks identical when not actively playing.
        if (thumb.currentTime > 0) {
          try { thumb.currentTime = 0 } catch (e) { /* HAVE_NOTHING */ }
        }
      }
      this._deactivateFullVideo(s)
      s.dataset.playing = 'false'
      s.dataset.userPaused = 'false'
    })
  }

  _getActiveVideo() {
    const slide = this._slides[this._activeIndex]
    if (!slide) return null
    // Prefer the full video when it's faded in, else fall back to thumb
    if (slide.dataset.fullReady === 'true') {
      return slide.querySelector('.sv-video--full') || slide.querySelector('video')
    }
    return slide.querySelector('video')
  }

  _playActive() {
    const activeSlide = this._slides[this._activeIndex]
    if (!activeSlide) return

    // Safety: never play on a clone. Should never trigger in practice because
    // the settle handler teleports off clones before this runs, but if a
    // clone somehow becomes active we just leave it on its poster.
    if (activeSlide.dataset.clone) return

    // Play thumbnail on active slide first (immediate visual while full video buffers)
    const activeThumb = activeSlide.querySelector('video')
    if (activeThumb && activeThumb.dataset.src) {
      this._injectSrc(activeSlide)
      activeThumb.muted = this._muted
      const doPlay = () => {
        activeThumb.play().catch(() => { })
        activeSlide.dataset.playing = 'true'
      }
      if (activeThumb.readyState >= 3) doPlay()
      else activeThumb.addEventListener('canplay', doPlay, { once: true })
    }

    // Seamlessly load + fade in the full video on top of the thumbnail
    this._activateFullVideo(activeSlide)

    // Play neighbour thumbnails — but ONLY for slides currently inside the
    // visible 5-card window (dist ≤ 2). The clone buffer at dist 3 stays
    // hidden via CSS, so playing those would just waste decoder bandwidth.
    // This caps concurrent playback at 5 (active full + active thumb + 4
    // visible inactive thumbs), matching the original 5-card behaviour even
    // though the DOM now contains 15 slides total. Clones inside the visible
    // window during a wraparound transition ARE played here too (they have
    // their own data-src now), so the brief moment before teleport looks
    // identical to the post-teleport real-slide state.
    for (let i = 0; i < this._slides.length; i++) {
      if (i === this._activeIndex) continue
      const neighbor = this._slides[i]
      const nDist = parseInt(neighbor.dataset.dist, 10)
      if (nDist > 2) continue
      const nVideo = neighbor.querySelector('video')
      if (!nVideo || !nVideo.dataset.src) continue
      this._injectSrc(neighbor)
      nVideo.muted = true
      const playNeighbor = () => {
        nVideo.play().catch(() => { })
        neighbor.dataset.playing = 'true'
      }
      if (nVideo.readyState >= 3) playNeighbor()
      else nVideo.addEventListener('canplay', playNeighbor, { once: true })
    }

    if (this._muted && !this._hintShown) this._showUnmuteHint()
  }

  _showUnmuteHint() {
    this._hintShown = true
    setTimeout(() => {
      this._slides.forEach(slide => {
        const slideHint = slide.querySelector('.sv-unmute-hint')
        if (slideHint) slideHint.dataset.dismissed = 'true'
      })
    }, 3000)
  }

  _clearPendingSettle() {
    if (this._settleHandler) {
      this._track.removeEventListener('transitionend', this._settleHandler)
      this._settleHandler = null
    }
    if (this._settleTimer) {
      clearTimeout(this._settleTimer)
      this._settleTimer = 0
    }
    this._isAnimating = false
  }

  _scheduleSettle() {
    const settle = () => {
      this._clearPendingSettle()

      // If the navigation landed on a clone (the user crossed past either
      // end of the real region), silently teleport to the matching real
      // slide. This must happen BEFORE we resume playback so we don't kick
      // off a video on a clone that's about to disappear.
      const landed = this._slides[this._activeIndex]
      if (landed?.dataset.clone) {
        this._teleportToReal(landed)
      }

      // Keep the active + immediate neighbours preloaded so the next move
      // can start playback instantly. Clones are skipped automatically by
      // _injectSrc (they have no data-src).
      this._injectSrcForRange(this._activeIndex, 1)
      if (this._loaded) this._ensureActivePlayback()
    }

    if (this._reducedMotion.matches) {
      requestAnimationFrame(settle)
      return
    }

    this._settleHandler = event => {
      if (event.target !== this._track || event.propertyName !== 'transform') return
      settle()
    }

    this._track.addEventListener('transitionend', this._settleHandler)
    this._settleTimer = setTimeout(settle, SV_SETTLE_FALLBACK_MS)
  }

  _rotateDomLeft(steps = 1, preservePosition = false) {
    const gap = this._getTrackGap()
    for (let i = 0; i < steps; i++) {
      const first = this._track.firstElementChild
      if (!first) break
      const shift = first.offsetWidth + gap
      this._track.append(first)
      this._activeIndex -= 1
      if (preservePosition) this._setTrackTransform(this._currentTranslateX + shift)
    }
    this._slides = [...this._track.querySelectorAll('.sv-slide')]
  }

  _rotateDomRight(steps = 1, preservePosition = false) {
    const gap = this._getTrackGap()
    for (let i = 0; i < steps; i++) {
      const last = this._track.lastElementChild
      if (!last) break
      const shift = last.offsetWidth + gap
      this._track.prepend(last)
      this._activeIndex += 1
      if (preservePosition) this._setTrackTransform(this._currentTranslateX - shift)
    }
    this._slides = [...this._track.querySelectorAll('.sv-slide')]
  }

  _normalizeOrder() {
    // No-op. The clone-based loop replaces DOM rotation: navigation that
    // crosses out of the real region into a clone is silently teleported
    // back to the matching real slide by _teleportToReal in the settle
    // handler. The DOM order never changes after _setupClones runs.
  }

  _ensureActivePlayback() {
    const activeSlide = this._slides[this._activeIndex]
    const video = activeSlide?.querySelector('video')
    if (!activeSlide || !video || !video.dataset.src) return
    if (!video.paused) {
      activeSlide.dataset.playing = 'true'
      return
    }
    this._playActive()
  }

  /* Animate to the slide at domIndex. The DOM order is FIXED — wraparound is
     handled by clones (see _setupClones) and a silent teleport in the settle
     handler (see _scheduleSettle). */
  _goTo(domIndex) {
    if (domIndex === this._activeIndex || this._isAnimating) return

    // Pause every video before the slide animation starts. On weak laptops,
    // 5+ video decoders running in parallel starves the GPU and the carousel
    // transform animation drops frames. The settle handler resumes playback
    // via _ensureActivePlayback() once the transition has finished, so the
    // user only sees a still frame for ~0.35s — the slide motion stays smooth.
    this._pauseAll()
    this._setActive(domIndex, true)
    this._injectSrcForRange(domIndex, 1)
    this._updateArrows()
    this._isAnimating = true
    this._scheduleSettle()
  }

  _step(delta) {
    if (this._realCount <= 1 || this._isAnimating) return

    // With clones in place the active is always at least _realCount slides
    // away from either DOM edge, so any single-step move is always within
    // bounds. Wraparound is handled by _teleportToReal in the settle handler:
    // navigating from real-N-1 to clone-real-0 lands on a clone, settle
    // teleports it to real-0 invisibly, and the user keeps clicking next.
    const target = this._activeIndex + delta
    if (target < 0 || target >= this._slides.length) return
    this._goTo(target)
  }

  _setupArrows() {
    this._prevArrow = this.querySelector('.sv-arrow--prev')
    this._nextArrow = this.querySelector('.sv-arrow--next')
    this._prevArrow?.addEventListener('click', () => this._step(-1))
    this._nextArrow?.addEventListener('click', () => this._step(1))
    // Position arrows now that refs exist (initial _repositionTrack ran before this)
    this._updateArrowPositions(this._viewport.offsetWidth, this._getTrackGap())
  }

  _updateArrows() {
    const single = this._count <= 1
    if (this._prevArrow) this._prevArrow.disabled = single
    if (this._nextArrow) this._nextArrow.disabled = single
  }

  _setupSlideClicks() {
    // Event delegation on the track. Binding per-slide doesn't work for the
    // clone strip — cloneNode does NOT copy event listeners, so any per-slide
    // listeners we bound before _setupClones() would be missing on the 10
    // clone slides. With delegation, one listener on the track catches every
    // .sv-video-card click regardless of whether it hit a real slide or a
    // clone, so navigating to a clone (which then silently teleports to its
    // real twin in settle) works exactly the same as navigating to a real.
    this._track.addEventListener('click', (e) => {
      const card = e.target.closest('.sv-video-card')
      if (!card) return
      const slide = card.closest('.sv-slide')
      if (!slide) return
      const currentIndex = this._slides.indexOf(slide)
      if (currentIndex < 0) return

      if (parseInt(slide.dataset.dist, 10) > 0) {
        this._goTo(currentIndex)
        return
      }

      // Click on the active slide → toggle play/pause on its video.
      // The active is always a real slide post-settle, so this never runs
      // against a clone (and even if it did, the clone has no src to play).
      const thumb = slide.querySelector('video')
      const full = slide.querySelector('.sv-video--full')
      if (!thumb) return
      if (thumb.paused) {
        thumb.play().catch(() => { })
        if (full && slide.dataset.fullReady === 'true') full.play().catch(() => { })
        slide.dataset.playing = 'true'
        slide.dataset.userPaused = 'false'
      } else {
        thumb.pause()
        if (full) full.pause()
        slide.dataset.playing = 'false'
        slide.dataset.userPaused = 'true'
      }
    })
  }

  _setupAtcButtons() {
    // Only bind to <button> elements — <a> tags (redirect case) navigate natively
    this.querySelectorAll('button.sv-atc-btn[data-action="atc"]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault()
        this._handleAtc(btn, btn.dataset.variantId)
      })
    })
  }

  _handleAtc(btn, variantId) {
    if (btn.disabled) return
    btn.dataset.state = 'loading'
    btn.disabled = true

    // Use the locale-aware root from Shopify's global routes object (handles
    // sub-folder locales like /en-us/, /fr-fr/ etc.)
    const root = window.Shopify?.routes?.root ?? '/'

    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: parseInt(variantId, 10),
        quantity: 1,
        // Bundled Section Rendering (Shopify AJAX Cart API):
        // Shopify renders the requested section server-side during the same
        // request and returns the HTML under response.sections[id].
        // We only need the bubble — no reason to render the full cart drawer
        // since we're keeping it closed.
        sections: 'cart-icon-bubble'
      })
    })
      .then(r => {
        // Treat non-2xx as errors (e.g. 422 sold out, 404 variant not found)
        if (!r.ok) return r.json().then(err => Promise.reject(err))
        return r.json()
      })
      .then(response => {
        btn.dataset.state = 'success'

        // Inject the server-rendered bubble HTML directly into the DOM.
        // Docs note: sections that fail to render return null — guard against it.
        const sectionHtml = response.sections?.['cart-icon-bubble']
        if (sectionHtml) {
          // On product pages the cart icon has id="cart-icon-bubble"; on other pages
          // (homepage, collections, etc.) it has no id — only the class. We must NOT
          // add the id to the non-product icon because cart-drawer.js uses that id to
          // attach a click handler that opens the drawer (which we never want on non-PDP).
          const bubble = document.getElementById('cart-icon-bubble') ?? document.querySelector('.header-cart-icon')
          const temp = document.createElement('template')
          temp.innerHTML = sectionHtml
          const content = temp.content.querySelector('.shopify-section')
          if (bubble && content) bubble.innerHTML = content.innerHTML
        }

        setTimeout(() => { btn.dataset.state = ''; btn.disabled = false }, 2000)
      })
      .catch(() => { btn.dataset.state = ''; btn.disabled = false })
  }

  _setupMuteBtn() {
    this.querySelectorAll('.sv-mute-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        this._muted = !this._muted
        this.dataset.muted = String(this._muted)
        // Toggle mute on both thumb and full of the active slide
        const slide = this._slides[this._activeIndex]
        if (!slide) return
        const thumb = slide.querySelector('video')
        const full = slide.querySelector('.sv-video--full')
        if (thumb) thumb.muted = this._muted
        if (full) full.muted = this._muted
      })
    })
  }

  _setupTouchSwipe() {
    let startX = 0, startY = 0, moved = false
    this._viewport.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX
      startY = e.touches[0].clientY
      moved = false
    }, { passive: true })
    this._viewport.addEventListener('touchmove', e => {
      if (!moved && Math.abs(e.touches[0].clientX - startX) > Math.abs(e.touches[0].clientY - startY)) {
        moved = true
      }
    }, { passive: true })
    this._viewport.addEventListener('touchend', e => {
      if (!moved) return
      const delta = startX - e.changedTouches[0].clientX
      if (Math.abs(delta) > 40) this._step(delta > 0 ? 1 : -1)
    }, { passive: true })
  }

  _setupResize() {
    let timer
    this._resizeHandler = () => {
      clearTimeout(timer)
      this._cachedGap = null
      timer = setTimeout(() => this._repositionTrack(false), 150)
    }
    window.addEventListener('resize', this._resizeHandler)
  }

  _setupThemeEditor() {
    this._themeEditorHandler = e => {
      // Find the real slide that the merchant just selected in the editor.
      const target = this._realSlides.find(s => s.dataset.index == e.detail?.blockIndex)
      if (!target) return
      const logicalIdx = this._getLogicalIndex(target)
      const domIdx = this._findRealDomIndex(logicalIdx)
      if (domIdx < 0) return

      this._clearPendingSettle()
      this._pauseAll()
      this._setActive(domIdx, false)
      this._injectSrcForRange(domIdx, 1)
      if (this._loaded) this._playActive()
    }
    document.addEventListener('shopify:block:select', this._themeEditorHandler)
  }

  /* ── Full-video overlay (seamless transition from thumbnail) ── */

  _activateFullVideo(slide) {
    const full = slide.querySelector('.sv-video--full')
    if (!full || !full.dataset.src) return

    full.muted = this._muted

    // Inject src if not yet loaded (first activation)
    if (!full.hasAttribute('src')) {
      full.preload = 'metadata'
      full.loop = true
      full.src = full.dataset.src
      full.load()

      // Belt-and-suspenders loop fix for dynamically injected src
      full.addEventListener('ended', () => {
        full.currentTime = 0
        full.play().catch(() => { })
      })
    }

    // When buffered, play and fade in on top of the thumbnail — no flash
    const onReady = () => {
      full.play().catch(() => { })
      slide.dataset.fullReady = 'true'
    }

    if (full.readyState >= 3) {
      onReady()
    } else {
      full.addEventListener('canplay', onReady, { once: true })
    }
  }

  _deactivateFullVideo(slide) {
    const full = slide.querySelector('.sv-video--full')
    if (full) full.pause()
    // CSS fade-out; src stays cached so re-activation is instant
    slide.dataset.fullReady = 'false'
  }

  /* ── Variant picker ── */

  _setupVariantPickers() {
    this._realSlides.forEach(slide => {
      const picker = slide.querySelector('.sv-variant-picker')
      if (!picker) return

      picker.querySelectorAll('.sv-swatch').forEach(swatch => {
        swatch.addEventListener('click', e => {
          e.preventDefault()
          e.stopPropagation()
          const pos = swatch.dataset.optionPosition
          picker.querySelectorAll(`.sv-swatch[data-option-position="${pos}"]`)
            .forEach(s => s.classList.remove('sv-swatch--active'))
          swatch.classList.add('sv-swatch--active')
          this._updateVariant(slide)
        })
      })

      picker.querySelectorAll('.sv-select').forEach(select => {
        select.addEventListener('change', e => {
          e.stopPropagation()
          this._updateVariant(slide)
        })
      })
    })
  }

  _getVariantData(slide) {
    if (slide._svVariants) return slide._svVariants
    const el = slide.querySelector('.sv-variant-data')
    if (!el) return null
    try { slide._svVariants = JSON.parse(el.textContent) }
    catch (e) { slide._svVariants = null }
    return slide._svVariants
  }

  _updateVariant(slide) {
    const variants = this._getVariantData(slide)
    if (!variants) return

    const sel = {}
    slide.querySelectorAll('.sv-swatch--active').forEach(s => {
      sel[s.dataset.optionPosition] = s.dataset.value
    })
    slide.querySelectorAll('.sv-select').forEach(s => {
      sel[s.dataset.optionPosition] = s.value
    })

    // Filter dependent dropdowns first
    this._filterOptions(slide, variants, sel)

    // Re-read dropdown values (may have changed after filtering)
    slide.querySelectorAll('.sv-select').forEach(s => {
      sel[s.dataset.optionPosition] = s.value
    })

    const match = variants.find(v => {
      if (sel['1'] && v.option1 !== sel['1']) return false
      if (sel['2'] && v.option2 !== sel['2']) return false
      if (sel['3'] && v.option3 !== sel['3']) return false
      return true
    })
    if (!match) return

    // Update ATC
    const btn = slide.querySelector('.sv-atc-btn[data-action="atc"]')
    if (btn) {
      btn.dataset.variantId = match.id
      btn.disabled = !match.available
    }

    // Update price
    const priceEl = slide.querySelector('.sv-price')
    const compareEl = slide.querySelector('.sv-compare')
    if (priceEl) priceEl.textContent = match.price_formatted
    if (compareEl) {
      if (match.compare_formatted) {
        compareEl.textContent = match.compare_formatted
        compareEl.style.display = ''
      } else {
        compareEl.style.display = 'none'
      }
    }

    // Update product image if variant has a featured image
    if (match.featured_image) {
      const img = slide.querySelector('.sv-product-img')
      if (img) img.src = match.featured_image
    }
  }

  _filterOptions(slide, variants, selections) {
    slide.querySelectorAll('.sv-select').forEach(select => {
      const pos = select.dataset.optionPosition
      const key = 'option' + pos
      const current = select.value

      const available = new Set()
      for (const v of variants) {
        if (!v.available) continue
        let ok = true
        for (const [p, val] of Object.entries(selections)) {
          if (p === pos) continue
          if (v['option' + p] !== val) { ok = false; break }
        }
        if (ok) available.add(v[key])
      }

      let currentValid = false
      for (const opt of select.options) {
        const hide = !available.has(opt.value)
        opt.hidden = hide
        opt.disabled = hide
        if (!hide && opt.value === current) currentValid = true
      }

      if (!currentValid) {
        const first = [...select.options].find(o => !o.hidden)
        if (first) select.value = first.value
      }
    })
  }
}

customElements.define('shoppable-video-section', ShoppableVideoSection)
